/**
 * リマインダ機能のLambdaにアクセスするためのドライバ関数群
 */

import { makeApiCall, createOptions } from '@/utils/comm';
import { getAuthInfo } from '@/awsCodes/awsUtils';
import { awsResources } from '@/awsCodes/resourceParams';
import { IResponse } from '@/appLogic/leanpath/interfaces';
import pako from 'pako';
import { Base64 } from 'js-base64';

/**
 * リマインダを新規登録する
 * @param date {string} 送信日時。YYYYMMDDの形式で指定
 * @param mailTo {string[]} 送信先メールアドレスの配列
 * @param mailFrom {string} 送信元メールアドレス
 * @param title {string} 件名
 * @param mailBody {string} 本文
 */
const createReminder = async (date: string, mailTo: string[], mailFrom: string, title: string, mailBody: string): Promise<IResponse> => {
  const res: any = await getAuthInfo();
  if (!res) throw new Error('refreshToken expired');
  const idToken = res.idToken;

  const restPath = awsResources.baseUrl['reminderManage'];
  const makeApiCallOptions = createOptions(idToken, `${restPath}/reminder`, {}, { cmd: 'CREATE', date, mailTo, mailFrom, title, mailBody });
  const { response }: any = await makeApiCall(makeApiCallOptions)
    .then((r: any) => ({ response: r }))
    .catch((e) => ({ error: e }));
  return response;
};

/**
 * リマインダを更新登録する
 * @param dataId {string} リマインダのユニーク識別子
 * @param date {string} 送信日時。YYYYMMDDの形式で指定
 * @param mailTo {string[]} 送信先メールアドレスの配列
 * @param mailFrom {string} 送信元メールアドレス
 * @param title {string} 件名
 * @param mailBody {string} 本文
 */
const updateReminder = async (
  dataId: string,
  date: string,
  mailTo: string[],
  mailFrom: string,
  title: string,
  mailBody: string
): Promise<IResponse> => {
  const res: any = await getAuthInfo();
  if (!res) throw new Error('refreshToken expired');
  const idToken = res.idToken;

  const restPath = awsResources.baseUrl['reminderManage'];
  const makeApiCallOptions = createOptions(
    idToken,
    `${restPath}/reminder`,
    {},
    { cmd: 'UPDATE', dataId, date, mailTo, mailFrom, title, mailBody }
  );
  const { response }: any = await makeApiCall(makeApiCallOptions)
    .then((r: any) => ({ response: r }))
    .catch((e) => ({ error: e }));
  return response;
};

/**
 * 組織のすべてのリマインダを取得する
 */
const getAllReminders = async (): Promise<IResponse> => {
  const res: any = await getAuthInfo();
  if (!res) throw new Error('refreshToken expired');
  const idToken = res.idToken;

  const restPath = awsResources.baseUrl['reminderManage'];
  const makeApiCallOptions = createOptions(idToken, `${restPath}/reminder`, {}, { cmd: 'READ' });
  const { response }: any = await makeApiCall(makeApiCallOptions)
    .then((r: any) => ({ response: r }))
    .catch((e) => ({ error: e }));

  if (response) {
    response.result = JSON.parse(pako.inflate(Base64.toUint8Array(response.result), { to: 'string' }));
  }
  return response;
};

/**
 * リマインダを削除する
 * @param dataId {string} リマインダのユニーク識別子
 */
const deleteReminder = async (dataId: string): Promise<IResponse> => {
  const res: any = await getAuthInfo();
  if (!res) throw new Error('refreshToken expired');
  const idToken = res.idToken;

  const restPath = awsResources.baseUrl['reminderManage'];
  const makeApiCallOptions = createOptions(idToken, `${restPath}/reminder`, {}, { cmd: 'DELETE', dataId });
  const { response }: any = await makeApiCall(makeApiCallOptions)
    .then((r: any) => ({ response: r }))
    .catch((e) => ({ error: e }));
  return response;
};

export { createReminder, updateReminder, getAllReminders, deleteReminder };
